// FASE 05 — S4-B2/PO 2026-05-13: service para a tela de Tabelas de Precos (Materiais).
// Decisao D1.b: chamadas vao para o backend legado (api/MOF.Server/Controllers/TabelaPrecoController.cs)
// via instancia `apiAntigo`.

import { apiAntigo } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import {
  ITabelaPrecoListagem,
  INovaTabelaPrecoPayload,
  IFiltroPrecificacao,
  IPrecificacao,
  IPrecoAlterado,
  IResultadoOperacao,
  IEstadoImportacao,
  ILogImportacao,
  IPlanilhaImportada,
  ITipoLiberado,
  OperacaoMassa,
  TipoMaterialTP,
} from "../types/tabelaPreco.types";

// FASE 05 — correcao 25/08/2026 (reporte do PO no usuario ROTO FERMAX: "o botao CRIAR nao faz nada").
//
// Duas causas, as duas aqui:
//
// 1) As rotas tinham "/api/" no inicio, mas a instancia apiAntigo JA traz "/api/" no baseURL.
//    O resultado era "/api/api/TabelaPreco/..." -> 404 em tudo (provado no log IIS do legado).
//    Todo o resto do sistema chama sem o prefixo (ex.: "/geral/selecaoTrocaUsuario").
//
// 2) `getErrorsByApi` SEMPRE lanca — e o contrato do helper compartilhado. Chamado direto no
//    catch, ele abortava a funcao antes do `return`, e a rejeicao subia sem tratamento ate a
//    tela: nenhuma notificacao, modal aberto, "nao aconteceu nada". Agora a mensagem e
//    capturada e devolvida, para a tela poder mostrar ao usuario.
const mensagemDeErro = (error: any, padrao: string): string => {
  try {
    getErrorsByApi(error, padrao);
    return padrao;
  } catch (lancado: any) {
    return lancado?.message || padrao;
  }
};

// GET /TabelaPreco/Lista (legado)
// Retorna todas as tabelas da empresa logada (IdEmpresaLogada vem do token JWT).
// Filtragem por tipo e feita no frontend ja que o legado retorna tudo.
export const listarTabelasPrecos = async (): Promise<ITabelaPrecoListagem[]> => {
  try {
    const { data } = await apiAntigo.get("/TabelaPreco/Lista");
    return (data || []).map((item: any) => ({
      id: item.id ?? item.Id,
      empresa: item.empresa ?? item.Empresa,
      referencia: item.referencia ?? item.Referencia ?? "",
      nome: item.nome ?? item.Nome ?? "",
      tipoProduto: item.tipoProduto ?? item.TipoProduto ?? "",
      dataAtualizacao: item.dataAtualizacao ?? item.DataAtualizacao ?? "",
      situacao: item.situacao ?? item.Situacao ?? "",
      possuiVersaoPublicada: item.possuiVersaoPublicada ?? item.PossuiVersaoPublicada ?? false,
    }));
  } catch (error: any) {
    console.error(mensagemDeErro(error, "Erro ao listar tabelas de preços"));
    return [];
  }
};

// POST /api/TabelaPreco (legado) — Salvar
// Cria nova tabela de precos com nome + tipo + referencia (situacao inicial: Em Edicao).
export const criarTabelaPreco = async (
  payload: INovaTabelaPrecoPayload
): Promise<{ sucesso: boolean; id?: number; mensagem?: string }> => {
  try {
    // O legado espera SalvarTabelaPreco com TabelasPrecos aninhado.
    const body = {
      tabelaPreco: {
        descricao: payload.nome,
        tipoMaterial: payload.idTipoMaterial,
        idReferencia: payload.idReferencia,
      },
      icms: 0,
      outroICMS: 0,
      vinculada: false,
      duplicada: false,
    };
    const { data } = await apiAntigo.post("/TabelaPreco", body);
    return { sucesso: true, id: data?.id ?? data?.Id, mensagem: "Tabela criada com sucesso" };
  } catch (error: any) {
    return { sucesso: false, mensagem: mensagemDeErro(error, "Erro ao criar tabela de preços") };
  }
};

// DELETE /api/TabelaPreco/{id} (legado)
export const excluirTabelaPreco = async (idTabelaPreco: number): Promise<boolean> => {
  try {
    await apiAntigo.delete(`/TabelaPreco/${idTabelaPreco}`);
    return true;
  } catch (error: any) {
    console.error(mensagemDeErro(error, "Erro ao excluir tabela de preços"));
    return false;
  }
};

// FASE 05 — S4-B2: a listagem so consegue localizar a tabela recem-criada relendo tudo,
// porque o POST do legado nao devolve o id. Usamos o nome + tipo para achar a mais recente.
export const localizarTabelaPorNome = async (
  nome: string,
  idTipoMaterial: number
): Promise<number | undefined> => {
  const tabelas = await listarTabelasPrecos();
  const alvo = tabelas
    .filter((t) => t.nome?.trim().toLowerCase() === nome.trim().toLowerCase())
    .filter((t) => TIPO_PRODUTO_PARA_ENUM[normalizarTipo(t.tipoProduto)] === idTipoMaterial);

  return alvo.length > 0 ? alvo[alvo.length - 1].id : undefined;
};

// FASE 05 — item 1 do relatorio do PO de 11/08 (diagnostico docs/17-08-2026):
// o legado devolve o tipo NO SINGULAR (MaterialEnum.Tipo tem [Display(Name = "Componente")],
// "Perfil", "Vidro", "Superficie", "Acabamento", "Kit"), enquanto o mapa antigo aqui so
// aceitava o plural. Resultado: toda linha virava null e o grid ficava vazio nas 6 abas —
// 39 tabelas de 17 empresas invisiveis. Agora normalizamos (minusculas, sem acento) e
// aceitamos as duas formas.
const normalizarTipo = (valor: string): string =>
  (valor || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const TIPO_DISPLAY_TO_TP: Record<string, TipoMaterialTP> = {
  perfil: "perfis",
  perfis: "perfis",
  componente: "componentes",
  componentes: "componentes",
  kit: "kits",
  kits: "kits",
  acabamento: "acabamentos",
  acabamentos: "acabamentos",
  superficie: "superficies",
  superficies: "superficies",
  vidro: "vidros",
  vidros: "vidros",
};

const TIPO_PRODUTO_PARA_ENUM: Record<string, number> = {
  perfil: 1,
  perfis: 1,
  componente: 2,
  componentes: 2,
  vidro: 3,
  vidros: 3,
  superficie: 4,
  superficies: 4,
  acabamento: 5,
  acabamentos: 5,
  kit: 6,
  kits: 6,
};

export const tipoProdutoDoBackendParaTP = (tipoProduto: string): TipoMaterialTP | null => {
  return TIPO_DISPLAY_TO_TP[normalizarTipo(tipoProduto)] ?? null;
};

// ---------------------------------------------------------------------------
// FASE 05 — S4-B3/B4/B5 (plano docs/19-08-2026): pagina de precificacao.
// Endpoints aditivos criados no legado para esta entrega.
// ---------------------------------------------------------------------------

// GET /api/TabelaPreco/PermissoesMof — chave "apenas para consulta" da empresa logada.
export const obterSomenteConsulta = async (): Promise<boolean> => {
  try {
    const { data } = await apiAntigo.get("/TabelaPreco/PermissoesMof");
    return Boolean(data?.somenteConsulta ?? data?.SomenteConsulta);
  } catch (error: any) {
    // Falha aqui nao pode travar a tela: assume o modo permissivo de sempre.
    console.error("Erro ao obter permissões de tabela de preços:", error);
    return false;
  }
};

// GET /TabelaPreco/TiposLiberados — abas conforme Gerência > Empresa > Tabelas de Preços
// (item 1 do reteste de 26/08).
export const listarTiposLiberados = async (): Promise<ITipoLiberado[]> => {
  try {
    const { data } = await apiAntigo.get("/TabelaPreco/TiposLiberados");
    return (data || []) as ITipoLiberado[];
  } catch (error: any) {
    console.error(mensagemDeErro(error, "Erro ao obter os tipos liberados"));
    return [];
  }
};

// GET /TabelaPreco/ItensPrecificacao/{id} — filtros e paginação resolvidos no servidor (TP-6).
// Devolve tambem o erro: antes a tela mostrava "não encontrada" para QUALQUER falha, o que
// mascarou um 500 real em 25/08 (Invalid column name 'Tratamentos').
export const obterPrecificacao = async (
  idTabelaPreco: number,
  filtro?: IFiltroPrecificacao
): Promise<{ dados: IPrecificacao | null; erro?: string }> => {
  try {
    const { data } = await apiAntigo.get(`/TabelaPreco/ItensPrecificacao/${idTabelaPreco}`, {
      params: {
        pagina: filtro?.pagina ?? 0,
        tamanhoPagina: filtro?.tamanhoPagina ?? 50,
        codigo: filtro?.codigo || undefined,
        descricao: filtro?.descricao || undefined,
        fornecedor: filtro?.fornecedor || undefined,
        linha: filtro?.linha || undefined,
        tipoKit: filtro?.tipoKit || undefined,
        status: filtro?.status ?? undefined,
        versaoPublicada: filtro?.versaoPublicada ?? false,
        somenteComPreco: filtro?.somenteComPreco ?? false,
      },
    });
    return { dados: data as IPrecificacao };
  } catch (error: any) {
    const erro = mensagemDeErro(error, "Erro ao carregar a tabela de preços");
    console.error(erro);
    return { dados: null, erro };
  }
};

// GET /TabelaPreco/ConsultaPublicada/{id} — versão publicada, só produtos com preço.
// É o que a tela mostra com o switch "Modo edição" desligado (item 7 do reteste).
export const obterConsultaPublicada = async (
  idTabelaPreco: number,
  filtro?: IFiltroPrecificacao
): Promise<{ dados: IPrecificacao | null; erro?: string }> => {
  try {
    const { data } = await apiAntigo.get(`/TabelaPreco/ConsultaPublicada/${idTabelaPreco}`, {
      params: {
        pagina: filtro?.pagina ?? 0,
        tamanhoPagina: filtro?.tamanhoPagina ?? 50,
        codigo: filtro?.codigo || undefined,
        descricao: filtro?.descricao || undefined,
        fornecedor: filtro?.fornecedor || undefined,
      },
    });
    return { dados: data as IPrecificacao };
  } catch (error: any) {
    const erro = mensagemDeErro(error, "Erro ao consultar a tabela de preços");
    console.error(erro);
    return { dados: null, erro };
  }
};

// POST /api/TabelaPreco/SalvarPrecos — grava apenas o delta editado na tela.
export const salvarPrecos = async (
  idTabelaPreco: number,
  itens: IPrecoAlterado[]
): Promise<IResultadoOperacao> => {
  try {
    const { data } = await apiAntigo.post("/TabelaPreco/SalvarPrecos", {
      idTabelaPreco,
      itens,
    });
    return data as IResultadoOperacao;
  } catch (error: any) {
    return { sucesso: false, total: 0, mensagem: mensagemDeErro(error, "Erro ao salvar os preços") };
  }
};

// POST /api/TabelaPreco/AlterarPrecosMassa
export const alterarPrecosMassa = async (
  idTabelaPreco: number,
  operacao: OperacaoMassa,
  valor: number,
  chaves: string[]
): Promise<IResultadoOperacao> => {
  try {
    const { data } = await apiAntigo.post("/TabelaPreco/AlterarPrecosMassa", {
      idTabelaPreco,
      operacao,
      valor,
      chaves,
    });
    return data as IResultadoOperacao;
  } catch (error: any) {
    return {
      sucesso: false,
      total: 0,
      mensagem: mensagemDeErro(error, "Erro ao alterar os preços em massa"),
    };
  }
};

// POST /api/TabelaPreco/HomologarTabela/{id}
export const homologarTabela = async (
  idTabelaPreco: number
): Promise<IResultadoOperacao> => {
  try {
    const { data } = await apiAntigo.post(`/TabelaPreco/HomologarTabela/${idTabelaPreco}`);
    return data as IResultadoOperacao;
  } catch (error: any) {
    return {
      sucesso: false,
      total: 0,
      mensagem: mensagemDeErro(error, "Erro ao homologar a tabela de preços"),
    };
  }
};

// POST /TabelaPreco/ConcluirHomologacaoTabela/{id} — Em Homologação → Homologada (TP-2).
export const concluirHomologacaoTabela = async (
  idTabelaPreco: number
): Promise<IResultadoOperacao> => {
  try {
    const { data } = await apiAntigo.post(
      `/TabelaPreco/ConcluirHomologacaoTabela/${idTabelaPreco}`
    );
    return data as IResultadoOperacao;
  } catch (error: any) {
    return {
      sucesso: false,
      total: 0,
      mensagem: mensagemDeErro(error, "Erro ao concluir a homologação da tabela"),
    };
  }
};

// POST /api/TabelaPreco/DisponibilizarTabela/{id}
export const disponibilizarTabela = async (
  idTabelaPreco: number
): Promise<IResultadoOperacao> => {
  try {
    const { data } = await apiAntigo.post(
      `/TabelaPreco/DisponibilizarTabela/${idTabelaPreco}`
    );
    return data as IResultadoOperacao;
  } catch (error: any) {
    return {
      sucesso: false,
      total: 0,
      mensagem: mensagemDeErro(error, "Erro ao disponibilizar a tabela de preços"),
    };
  }
};

// GET /api/TabelaPreco/ExportarPrecificacao/{id} — estado atual para gerar o arquivo.
//
// Plano 30/08/2026, Frente 2 (reporte do TI de 30/08): a chamada nao enviava filtro nenhum e a
// planilha saia com a tabela inteira, mesmo depois de o usuario ter feito uma busca. Agora vai
// o mesmo filtro do grid. A paginacao NAO e enviada de proposito: exporta-se o resultado
// completo da busca, nao a pagina visivel.
export const exportarPrecificacao = async (
  idTabelaPreco: number,
  filtro?: IFiltroPrecificacao
): Promise<
  Array<{
    codigo: string;
    cor: string;
    descricao: string;
    valor: number;
    taxa?: number | null;
    medida?: string | null;
    // Plano 28/10/2026, Frente C: espessura dos Kits (volta importável).
    espessura?: number | null;
    // Plano 08/09/2026: tamanho da barra dos Perfis em BR (volta importável).
    tamanhoBarra?: number | null;
  }>
> => {
  try {
    const { data } = await apiAntigo.get(
      `/TabelaPreco/ExportarPrecificacao/${idTabelaPreco}`,
      {
        params: {
          codigo: filtro?.codigo || undefined,
          descricao: filtro?.descricao || undefined,
          fornecedor: filtro?.fornecedor || undefined,
          linha: filtro?.linha || undefined,
          tipoKit: filtro?.tipoKit || undefined,
          status: filtro?.status ?? undefined,
          versaoPublicada: filtro?.versaoPublicada || undefined,
          somenteComPreco: filtro?.somenteComPreco || undefined,
        },
      }
    );
    return data || [];
  } catch (error: any) {
    console.error(mensagemDeErro(error, "Erro ao exportar a tabela de preços"));
    return [];
  }
};

// POST /api/TabelaPreco/ImportarPlanilha — endpoint que JA existia no legado.
// Recebe o arquivo e o conteudo ja lido em JSON (mesmo contrato do MOF antigo).
export const importarPlanilha = async (parametros: {
  idTabelaPreco: number;
  descricao: string;
  referencia: string;
  idTipoMaterial: number;
  arquivo: File;
  conteudo: string;
}): Promise<{ sucesso: boolean; log?: ILogImportacao; mensagem?: string }> => {
  try {
    const form = new FormData();
    form.append("Id", String(parametros.idTabelaPreco));
    form.append("Descricao", parametros.descricao);
    form.append("Referencia", parametros.referencia ?? "");
    form.append("TipoMaterial", String(parametros.idTipoMaterial));
    form.append("Arquivo", parametros.arquivo);
    form.append("Conteudo", parametros.conteudo);

    const { data } = await apiAntigo.post("/TabelaPreco/ImportarPlanilha", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return { sucesso: true, log: data as ILogImportacao };
  } catch (error: any) {
    return { sucesso: false, mensagem: mensagemDeErro(error, "Erro ao importar a planilha") };
  }
};

// ---------------------------------------------------------------------------
// Plano 14/10/2026, Frentes 2 e 3: trava planilha × edição manual, "Última planilha importada"
// (lixeira) e "Histórico de importação". Endpoints aditivos do legado — rotas SEM "/api/"
// (ver o comentário do topo).
// ---------------------------------------------------------------------------

const mapearPlanilha = (item: any): IPlanilhaImportada => ({
  id: item?.id ?? item?.Id,
  dataHora: item?.dataHora ?? item?.DataHora ?? "",
  situacao: item?.situacao ?? item?.Situacao ?? "",
  link: item?.link ?? item?.Link ?? "",
});

// GET /TabelaPreco/EstadoImportacao/{id}
export const obterEstadoImportacao = async (
  idTabelaPreco: number
): Promise<IEstadoImportacao | null> => {
  try {
    const { data } = await apiAntigo.get(`/TabelaPreco/EstadoImportacao/${idTabelaPreco}`);
    const pendente = data?.planilhaPendente ?? data?.PlanilhaPendente;
    return {
      planilhaPendente: pendente ? mapearPlanilha(pendente) : null,
      edicaoManualPendente: Boolean(data?.edicaoManualPendente ?? data?.EdicaoManualPendente),
    };
  } catch (error: any) {
    // Sem o estado a tela segue como antes; o servidor continua aplicando a trava.
    console.error(mensagemDeErro(error, "Erro ao obter o estado da importação"));
    return null;
  }
};

// GET /TabelaPreco/HistoricoImportacao/{id}
export const obterHistoricoImportacao = async (
  idTabelaPreco: number
): Promise<{ itens: IPlanilhaImportada[]; erro?: string }> => {
  try {
    const { data } = await apiAntigo.get(`/TabelaPreco/HistoricoImportacao/${idTabelaPreco}`);
    return { itens: (data || []).map(mapearPlanilha) };
  } catch (error: any) {
    return { itens: [], erro: mensagemDeErro(error, "Erro ao carregar o histórico de importação") };
  }
};

// DELETE /TabelaPreco/DesfazerImportacao/{id} — lixeira da "Última planilha importada".
export const desfazerImportacao = async (idTabelaPreco: number): Promise<IResultadoOperacao> => {
  try {
    const { data } = await apiAntigo.delete(`/TabelaPreco/DesfazerImportacao/${idTabelaPreco}`);
    return data as IResultadoOperacao;
  } catch (error: any) {
    return {
      sucesso: false,
      total: 0,
      mensagem: mensagemDeErro(error, "Erro ao excluir a planilha importada"),
    };
  }
};
